import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  User, Briefcase, FileBadge, Star, MessageSquare, History,
  ShieldCheck, Activity, ArrowLeft, FileCheck2, ExternalLink,
} from "lucide-react";

const TABS = [
  { key: "personal", label: "Personal", icon: User },
  { key: "employment", label: "Employment", icon: Briefcase },
  { key: "certs", label: "Certificates", icon: FileBadge },
  { key: "conduct", label: "Conduct", icon: Star },
  { key: "recs", label: "Recommendations", icon: MessageSquare },
  { key: "timeline", label: "Timeline", icon: History },
  { key: "verify", label: "Verification", icon: ShieldCheck },
  { key: "activity", label: "Activity", icon: Activity },
];

export default function EmployeeProfilePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("personal");

  useEffect(() => {
    api.get(`/records/employees/${id}/profile`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load."));
  }, [id]);

  if (err) return <WorkspaceLayout title="Employee profile"><div className="p-8 text-ec-error" data-testid="profile-error">{err}</div></WorkspaceLayout>;
  if (!data) return <WorkspaceLayout title="Employee profile"><div className="p-8 text-ec-ink-soft">Loading…</div></WorkspaceLayout>;

  const rec = data.record;
  const u = data.linked_user;
  const score = data.score;

  return (
    <WorkspaceLayout
      subtitle="HR workspace"
      title={rec.full_name}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm gap-2" onClick={() => nav("/workspace/employees")} data-testid="profile-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <Link to={`/workspace/certificates/issue?record=${rec.id}`}>
            <Button className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-2" data-testid="profile-issue-cert">
              <FileCheck2 className="w-4 h-4" /> Issue Certificate
            </Button>
          </Link>
        </div>
      }
    >
      <ProfileHeader rec={rec} u={u} score={score} />

      <div className="flex flex-wrap gap-px bg-ec-line border border-ec-line mb-6 mt-6" data-testid="profile-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          const count = countFor(t.key, data);
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 min-w-[140px] px-3 py-3 text-left bg-white transition-colors ${active ? "bg-ec-emerald/5 border-b-2 border-ec-emerald" : "hover:bg-ec-mute"}`}
              data-testid={`profile-tab-${t.key}`}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-3.5 h-3.5 text-ec-ink-soft" />
                <span className="eyebrow">{t.label}</span>
                {count !== null && <span className="ml-auto text-[10px] font-mono-plex text-ec-ink-soft">{count}</span>}
              </div>
            </button>
          );
        })}
      </div>

      <div>
        {tab === "personal" && <PersonalTab rec={rec} u={u} />}
        {tab === "employment" && <EmploymentTab rec={rec} />}
        {tab === "certs" && <CertsTab certs={data.certificates} />}
        {tab === "conduct" && <ConductTab certs={data.conduct_certificates} />}
        {tab === "recs" && <RecsTab recs={data.recommendations} />}
        {tab === "timeline" && <TimelineTab timeline={data.timeline} />}
        {tab === "verify" && <VerifyTab history={data.verification_history} />}
        {tab === "activity" && <ActivityTab activity={data.activity} />}
      </div>
    </WorkspaceLayout>
  );
}

function countFor(key, data) {
  switch (key) {
    case "certs": return data.certificates?.length || 0;
    case "conduct": return data.conduct_certificates?.length || 0;
    case "recs": return data.recommendations?.length || 0;
    case "timeline": return data.timeline?.length || 0;
    case "verify": return data.verification_history?.length || 0;
    case "activity": return data.activity?.length || 0;
    default: return null;
  }
}

function ProfileHeader({ rec, u, score }) {
  const initials = (rec.full_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  return (
    <Card className="rounded-sm border-ec-line" data-testid="profile-header">
      <CardContent className="p-6 flex flex-wrap items-start gap-5">
        {u?.avatar_url ? (
          <img src={u.avatar_url} alt={rec.full_name} className="w-20 h-20 rounded-sm border border-ec-line object-cover" />
        ) : (
          <div className="w-20 h-20 rounded-sm bg-ec-ink text-white flex items-center justify-center font-display text-2xl font-black">{initials}</div>
        )}
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl font-black">{rec.full_name}</h2>
            <StatusBadge status={rec.status} extra={rec.employment_status} />
            {u?.ethic_id && <Badge className="rounded-sm bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30 font-mono-plex text-[10px]">{u.ethic_id}</Badge>}
          </div>
          <div className="text-sm text-ec-ink-soft mt-1">{rec.designation} · {rec.department} · {rec.employment_type}</div>
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            <KV label="Employee ID" value={rec.employee_id} />
            <KV label="Email" value={rec.personal_email} />
            <KV label="Phone" value={rec.phone} />
            {rec.work_location && <KV label="Location" value={rec.work_location} />}
            {rec.branch && <KV label="Branch" value={rec.branch} />}
            {rec.reporting_manager && <KV label="Reports to" value={rec.reporting_manager} />}
          </div>
          {score && (
            <div className="flex gap-2 mt-3">
              <KV label="Trust Tier" value={score.tier} highlight />
              <KV label="Score" value={`${score.score} / ${score.max_score}`} highlight />
            </div>
          )}
          {u?.ethic_id && (
            <a href={`/passport/${u.ethic_id}`} target="_blank" rel="noreferrer" className="text-xs text-ec-emerald hover:underline mt-3 inline-flex items-center gap-1" data-testid="profile-passport-link">
              View public passport <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function KV({ label, value, highlight }) {
  return (
    <div className={`px-2 py-1 rounded-sm border ${highlight ? "border-ec-emerald/40 bg-ec-emerald/5" : "border-ec-line bg-white"} text-[11px]`}>
      <span className="text-ec-ink-soft">{label}: </span><span className="font-semibold text-ec-ink">{value || "—"}</span>
    </div>
  );
}

function StatusBadge({ status, extra }) {
  const styles = {
    "Linked": "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30",
    "Unclaimed": "bg-ec-ink-soft/10 text-ec-ink-soft border-ec-ink-soft/30",
    "Claim Requested": "bg-amber-500/10 text-amber-700 border-amber-500/40",
    "Disputed": "bg-ec-error/10 text-ec-error border-ec-error/30",
  };
  const empStyles = {
    "Active":     "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30",
    "Probation":  "bg-blue-100 text-blue-800 border-blue-300",
    "On Leave":   "bg-amber-100 text-amber-800 border-amber-300",
    "Resigned":   "bg-zinc-200 text-zinc-700 border-zinc-300",
    "Terminated": "bg-ec-error/10 text-ec-error border-ec-error/30",
    "Absconded":  "bg-ec-error/10 text-ec-error border-ec-error/30",
    "Retired":    "bg-purple-100 text-purple-800 border-purple-300",
  };
  return (
    <div className="flex gap-2 items-center">
      <Badge className={`rounded-sm border ${styles[status] || ""}`}>{status}</Badge>
      {extra && <Badge className={`rounded-sm border ${empStyles[extra] || "border-ec-line text-ec-ink-soft bg-white"}`}>{extra}</Badge>}
    </div>
  );
}

// ---------------- Tabs ----------------
function PersonalTab({ rec, u }) {
  return (
    <Card className="rounded-sm border-ec-line" data-testid="tab-personal">
      <CardContent className="p-6 grid sm:grid-cols-2 gap-4">
        <Row label="Full Name" value={rec.full_name} />
        <Row label="Personal Email" value={rec.personal_email} />
        <Row label="Phone" value={rec.phone} />
        <Row label="Official Email" value={rec.official_email} />
        <Row label="Date of Birth" value={rec.date_of_birth} />
        <Row label="Emergency Contact" value={rec.emergency_contact} />
        <Row label="PAN (masked)" value={rec.pan_masked} />
        <Row label="EthicCred ID" value={u?.ethic_id} />
        <Row label="Linked at" value={rec.linked_at} />
      </CardContent>
    </Card>
  );
}

function EmploymentTab({ rec }) {
  return (
    <Card className="rounded-sm border-ec-line" data-testid="tab-employment">
      <CardContent className="p-6 grid sm:grid-cols-2 gap-4">
        <Row label="Employee ID" value={rec.employee_id} />
        <Row label="Designation" value={rec.designation} />
        <Row label="Department" value={rec.department} />
        <Row label="Employment Type" value={rec.employment_type} />
        <Row label="Joining Date" value={rec.joining_date} />
        <Row label="Job End Date" value={rec.job_end_date} />
        <Row label="Exit Reason" value={rec.exit_reason} />
        <Row label="Reporting Manager" value={rec.reporting_manager} />
        <Row label="Branch" value={rec.branch} />
        <Row label="Work Location" value={rec.work_location} />
        <Row label="Status" value={rec.employment_status} />
      </CardContent>
    </Card>
  );
}

function CertsTab({ certs }) {
  if (!certs?.length) return <EmptyState>No certificates issued yet.</EmptyState>;
  return (
    <div className="space-y-3" data-testid="tab-certs">
      {certs.map((c) => <CertRow key={c.id} c={c} />)}
    </div>
  );
}
function ConductTab({ certs }) {
  if (!certs?.length) return <EmptyState>No conduct certificates yet.</EmptyState>;
  return (
    <div className="space-y-3" data-testid="tab-conduct">
      {certs.map((c) => <CertRow key={c.id} c={c} />)}
    </div>
  );
}
function CertRow({ c }) {
  return (
    <Card className="rounded-sm border-ec-line">
      <CardContent className="p-4 flex flex-wrap gap-3 items-start" data-testid={`cert-row-${c.id}`}>
        <FileBadge className="w-5 h-5 text-ec-emerald mt-0.5" />
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold">{c.title} <Badge className="ml-1 rounded-sm bg-ec-mute border-ec-line text-ec-ink-soft text-[10px]">{c.cert_type}{c.sub_category ? ` · ${c.sub_category}` : ""}</Badge></div>
          <div className="text-xs text-ec-ink-soft mt-1">{c.description}</div>
          <div className="text-[11px] font-mono-plex text-ec-ink-soft mt-2 flex flex-wrap gap-3">
            <span>#{c.certificate_no || c.id.slice(0, 8)}</span>
            {c.duration?.text && <span>Duration: {c.duration.text}</span>}
            {c.issue_date && <span>Issued: {c.issue_date}</span>}
            <span>Status: {c.approval_status || (c.revoked ? "Revoked" : c.acceptance_status || "Issued")}</span>
          </div>
        </div>
        <a href={`/verify/${c.id}`} target="_blank" rel="noreferrer" className="text-xs text-ec-emerald hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Verify</a>
      </CardContent>
    </Card>
  );
}

function RecsTab({ recs }) {
  if (!recs?.length) return <EmptyState>No recommendations yet.</EmptyState>;
  return (
    <div className="space-y-3" data-testid="tab-recs">
      {recs.map((r) => (
        <Card key={r.id} className="rounded-sm border-ec-line">
          <CardContent className="p-4">
            <div className="text-sm">{r.message}</div>
            <div className="text-[11px] text-ec-ink-soft mt-2 font-mono-plex">— {r.author_name} ({r.relationship}{r.author_org ? `, ${r.author_org}` : ""}) · {r.created_at?.slice(0, 10)}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TimelineTab({ timeline }) {
  if (!timeline?.length) return <EmptyState>No employment history linked to this passport yet.</EmptyState>;
  return (
    <ol className="border-l-2 border-ec-emerald/30 pl-5 space-y-4" data-testid="tab-timeline">
      {timeline.map((t) => (
        <li key={t.id} className="relative">
          <div className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-ec-emerald" />
          <Card className="rounded-sm border-ec-line">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-semibold">{t.designation}</div>
                <Badge className="rounded-sm border-ec-line bg-ec-mute text-ec-ink-soft">{t.employment_type}</Badge>
                <Badge className="rounded-sm border-ec-line bg-white">{t.employment_status}</Badge>
              </div>
              <div className="text-xs text-ec-ink-soft mt-1">{t.issuer_name} · {t.department} · {t.work_location}</div>
              <div className="text-[11px] font-mono-plex text-ec-ink-soft mt-1">{t.joining_date} → {t.job_end_date || "present"}</div>
              {t.exit_reason && <div className="text-[11px] text-ec-error mt-1">Exit: {t.exit_reason}</div>}
            </CardContent>
          </Card>
        </li>
      ))}
    </ol>
  );
}

function VerifyTab({ history }) {
  if (!history?.length) return <EmptyState>No verification activity recorded.</EmptyState>;
  return <LogList items={history} testid="tab-verify" />;
}

function ActivityTab({ activity }) {
  if (!activity?.length) return <EmptyState>No activity logged.</EmptyState>;
  return <LogList items={activity} testid="tab-activity" />;
}

function LogList({ items, testid }) {
  return (
    <Card className="rounded-sm border-ec-line" data-testid={testid}>
      <CardContent className="p-0">
        <ul className="divide-y divide-ec-line">
          {items.map((a) => (
            <li key={a.id || a.timestamp} className="px-4 py-3 flex items-center gap-3 text-sm">
              <Activity className="w-3.5 h-3.5 text-ec-ink-soft" />
              <span className="font-semibold">{a.event_type}</span>
              <span className="text-ec-ink-soft text-xs flex-1">{a.meta ? JSON.stringify(a.meta).slice(0, 80) : ""}</span>
              <span className="text-[10px] font-mono-plex text-ec-ink-soft">{a.timestamp?.slice(0, 19).replace("T", " ")}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function EmptyState({ children }) {
  return (
    <Card className="rounded-sm border-ec-line">
      <CardContent className="p-10 text-center text-ec-ink-soft">{children}</CardContent>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div className="text-sm">
      <div className="eyebrow text-ec-ink-soft">{label}</div>
      <div className="text-ec-ink mt-1">{value || "—"}</div>
    </div>
  );
}
